import { Link } from 'react-router-dom';
import { FaYoutube, FaTiktok, FaFacebook } from 'react-icons/fa';

const AboutPage = () => {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
              Our Story
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Brissbella is a modern Sri Lankan beauty and wellness brand that
              integrates professional training, herbal self-care, and
              high-standard salon services under one purpose-driven umbrella.
            </p>
          </div>
        </div>
      </section>

      {/* Brand Journey */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-primary-600">
                Our Journey
              </h2>
              <p className="text-gray-700">
                Founded in 2020 by Mashi Nehansa, a qualified beautician and
                educator, Brissbella has grown from a single salon in Kandy into
                a multi-branch business that now includes a recognized beauty
                academy, a herbal product line, and a VIP unisex salon serving
                clients locally and internationally.
              </p>
              <p className="text-gray-700">
                We combine technical quality with deep cultural relevance,
                aiming not just to meet market demand but to elevate how beauty
                is taught, practiced, and experienced in Sri Lanka.
              </p>
            </div>
            <div className="relative">
              <img
                src="/images/about/journey.jpg"
                alt="Brissbella Journey"
                className="w-full h-full object-contain rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Values */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary-600">
              Our Vision & Values
            </h2>
            <p className="text-xl text-gray-700 mt-4 max-w-3xl mx-auto">
              We combine technical quality with deep cultural relevance, aiming
              to elevate how beauty is taught, practiced, and experienced in Sri
              Lanka.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-xl font-semibold mb-4">Excellence</h3>
              <p className="text-gray-600">
                "Excellence isn't an option, it's a choice." We maintain the
                highest standards in education, services, and product quality.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-xl font-semibold mb-4">Innovation</h3>
              <p className="text-gray-600">
                Blending traditional wisdom with modern techniques, we stay at
                the forefront of beauty industry developments.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-xl font-semibold mb-4">Empowerment</h3>
              <p className="text-gray-600">
                We believe in creating opportunities for women to learn, earn,
                and lead in the beauty industry.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section - Updated Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">
            Our Growth Story
          </h2>

          {/* Timeline Container */}
          <div className="relative">
            {/* Desktop Timeline Line */}
            <div className="hidden md:block absolute left-0 top-8 w-full h-1 bg-gradient-to-r from-primary-100 via-primary-300 to-primary-100"></div>

            {/* Timeline Items Container */}
            <div className="flex flex-col md:flex-row md:justify-between space-y-8 md:space-y-0">
              {/* 2020 */}
              <div className="flex flex-col items-center relative group md:w-1/6">
                <div className="w-16 h-16 bg-white rounded-full border-4 border-primary-600 z-10 flex items-center justify-center group-hover:bg-primary-50 transition-colors duration-300">
                  <span className="text-primary-600 font-bold text-lg">
                    2020
                  </span>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-gray-600 text-sm">
                    Launched First Salon
                    <br />
                    in Kandy
                  </p>
                </div>
              </div>

              {/* 2021 */}
              <div className="flex flex-col items-center relative group md:w-1/6">
                <div className="w-16 h-16 bg-white rounded-full border-4 border-primary-600 z-10 flex items-center justify-center group-hover:bg-primary-50 transition-colors duration-300">
                  <span className="text-primary-600 font-bold text-lg">
                    2021
                  </span>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-gray-600 text-sm">
                    Introduced Herbal
                    <br />
                    Product Line &<br />
                    Online Store
                  </p>
                </div>
              </div>

              {/* 2022 */}
              <div className="flex flex-col items-center relative group md:w-1/6">
                <div className="w-16 h-16 bg-white rounded-full border-4 border-primary-600 z-10 flex items-center justify-center group-hover:bg-primary-50 transition-colors duration-300">
                  <span className="text-primary-600 font-bold text-lg">
                    2022
                  </span>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-gray-600 text-sm">
                    Upgraded to VIP Salon
                    <br />
                    & Launched Academy
                    <br />
                    First Batch: 20 Students
                  </p>
                </div>
              </div>

              {/* 2023 */}
              <div className="flex flex-col items-center relative group md:w-1/6">
                <div className="w-16 h-16 bg-white rounded-full border-4 border-primary-600 z-10 flex items-center justify-center group-hover:bg-primary-50 transition-colors duration-300">
                  <span className="text-primary-600 font-bold text-lg">
                    2023
                  </span>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-gray-600 text-sm">
                    50+ Graduates
                    <br />
                    Amaya Hills Ceremony
                    <br />
                    10K+ Social Following
                  </p>
                </div>
              </div>

              {/* 2024 */}
              <div className="flex flex-col items-center relative group md:w-1/6">
                <div className="w-16 h-16 bg-white rounded-full border-4 border-primary-600 z-10 flex items-center justify-center group-hover:bg-primary-50 transition-colors duration-300">
                  <span className="text-primary-600 font-bold text-lg">
                    2024
                  </span>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-gray-600 text-sm">
                    100+ Graduates
                    <br />
                    BMICH Ceremony
                    <br />
                    150K YouTube Milestone
                  </p>
                </div>
              </div>

              {/* 2025 */}
              <div className="flex flex-col items-center relative group md:w-1/6">
                <div className="w-16 h-16 bg-white rounded-full border-4 border-primary-600 z-10 flex items-center justify-center group-hover:bg-primary-50 transition-colors duration-300">
                  <span className="text-primary-600 font-bold text-lg">
                    2025
                  </span>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-gray-600 text-sm">
                    200+ Graduates
                    <br />
                    Galadari Ceremony
                    <br />
                    Island-wide Recognition
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Verticals */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Our Business Verticals
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Academy Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img
                src="/images/about/academy.jpg"
                alt="Brissbella Academy"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  Brissbella Academy
                </h3>
                <p className="text-gray-600">
                  Vocational beauty education centers in Kandy and Colombo
                  offering NVQ-aligned diplomas, certificate programs, and
                  global exposure through partnerships like Riva Institute of
                  Aesthetics (Dubai).
                </p>
                <Link
                  to="/academy"
                  className="mt-4 inline-block text-primary-600 hover:text-primary-700"
                >
                  Learn more →
                </Link>
              </div>
            </div>

            {/* Wellness Products Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img
                src="/images/about/products.jpg"
                alt="Wellness Products"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  Wellness Products
                </h3>
                <p className="text-gray-600">
                  A line of 100% herbal beauty products made from Ayurvedic
                  ingredients and Sri Lankan botanicals, available island-wide
                  and through an active online community.
                </p>
                <Link
                  to="/store"
                  className="mt-4 inline-block text-primary-600 hover:text-primary-700"
                >
                  Learn more →
                </Link>
              </div>
            </div>

            {/* VIP Salon Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img
                src="/images/about/salon.jpg"
                alt="VIP Salon"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-4">VIP Salon</h3>
                <p className="text-gray-600">
                  A full-service premium beauty salon in Kandy, offering hair,
                  skin, nail, and bridal treatments with a personal, high-touch
                  experience.
                </p>
                <Link
                  to="/services"
                  className="mt-4 inline-block text-primary-600 hover:text-primary-700"
                >
                  Learn more →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder's Story */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-primary-600">
                Founder's Story
              </h2>
              <div className="prose prose-lg">
                <p className="text-gray-700">
                  Mashi Nehansa's journey in beauty began with a simple toolkit
                  and a powerful vision. As a qualified beautician with NVQ
                  Level 4 certification and high diplomas in Beauty Culture,
                  Hairdressing, and Bridal Dressing, she saw beyond the surface
                  of beauty services.
                </p>

                <blockquote className="text-xl italic border-l-4 border-primary-600 pl-4 my-6">
                  "I wanted to create something that would last beyond me. A
                  system where women could learn, earn, and lead. Where beauty
                  wasn't about perfection, but power."
                </blockquote>

                <p className="text-gray-700">
                  Through years of hands-on experience, Mashi identified
                  critical gaps in Sri Lanka's beauty industry: talented
                  individuals lacking professional pathways, women with untapped
                  potential, and a market yearning for authentic, natural beauty
                  solutions.
                </p>

                <div className="bg-white p-6 rounded-lg shadow-sm my-6">
                  <h3 className="text-lg font-semibold mb-4">
                    Core Commitments
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="text-primary-600 mr-2">•</span>
                      Personal involvement in academy training
                    </li>
                    <li className="flex items-center">
                      <span className="text-primary-600 mr-2">•</span>
                      Development of natural herbal formulations
                    </li>
                    <li className="flex items-center">
                      <span className="text-primary-600 mr-2">•</span>
                      Hands-on approach to bridal styling
                    </li>
                    <li className="flex items-center">
                      <span className="text-primary-600 mr-2">•</span>
                      Leading digital beauty education
                    </li>
                  </ul>
                </div>

                <p className="text-gray-700">
                  Today, Mashi's influence extends far beyond the salon chair.
                  With over 150,000 YouTube subscribers and 100,000+ TikTok
                  followers, she has become a trusted voice in Sri Lanka's
                  beauty education landscape. Her vision for Brissbella isn't
                  just about building a successful business—it's about creating
                  a sustainable ecosystem that empowers through beauty education
                  and authentic care.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <img
                src="/images/about/founder.png"
                alt="Mashi Nehansa"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Our Achievements
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">
                1000+
              </div>
              <p className="text-gray-600">Professionals Trained</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">
                150K+
              </div>
              <p className="text-gray-600">YouTube Subscribers</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">
                100K+
              </div>
              <p className="text-gray-600">TikTok Followers</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">
                40K+
              </div>
              <p className="text-gray-600">Facebook Community</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8">Connect With Us</h2>
          <div className="flex justify-center space-x-8">
            <a
              href="https://youtube.com/@BrissbellaAcademy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700"
            >
              <FaYoutube size={40} />
            </a>
            <a
              href="https://www.tiktok.com/@brissbella.academy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700"
            >
              <FaTiktok size={40} />
            </a>
            <a
              href="https://www.facebook.com/brissbellaacademy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700"
            >
              <FaFacebook size={40} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
